import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  Divider,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../api/axiosConfig';
import { useTranslation } from 'react-i18next';

const Importar = () => {
  const { t } = useTranslation();

  // Helper to translate backend message keys
  const translateBackendMessage = (message, data = {}) => {
    const messageMap = {
      'PLAYER_ALREADY_EXISTS': t('import.playerAlreadyExists', { name: data.playerName || '' }),
      'PLAYER_IMPORTED_SUCCESS': t('import.playerImportedSuccess'),
      'PLAYER_IMPORT_ERROR': t('import.playerImportError')
    };
    return messageMap[message] || message;
  };

  const [formData, setFormData] = useState({
    details: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [playersData, setPlayersData] = useState([]);
  const [logs, setLogs] = useState([]);

  // Pour la fenêtre de dialogue (résultats multiples Eurobasket)
  const [currentPlayerName, setCurrentPlayerName] = useState('');

  // Pour la fenêtre de dialogue (résultats multiples Eurobasket)
  const [openEurobasketDialog, setOpenEurobasketDialog] = useState(false);
  const [eurobasketOptions, setEurobasketOptions] = useState([]);
  const [pendingPlayerData, setPendingPlayerData] = useState(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  const [proballersPlayersData, setProballersPlayersData] = useState([]);

  // Référence pour le scroll automatique des logs
  const logsEndRef = useRef(null);

  // Variables d'environnement
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  // Scroll automatique vers le bas quand les logs changent
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Import Proballers (SSE)
  const handleProballersImport = (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    setPlayersData([]);
    setLogs([]);

    // Récupérer le club_id et la langue depuis localStorage pour le multi-tenant
    const clubId = localStorage.getItem('clubId');
    const lang = localStorage.getItem('language') || 'es';

    const url = `${backendUrl}/api/importar/proballers?details=${encodeURIComponent(
      formData.details
    )}&section=${encodeURIComponent(section)}${clubId ? `&club_id=${clubId}` : ''}&lang=${lang}`;

    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'log') {
          setLogs((prevLogs) => [...prevLogs, data.message]);
        } else if (data.type === 'done') {
          // Vérifier si certains joueurs ont multipleEurobasket
          const playersWithEurobasketChoice = data.players.filter(p => p.multipleEurobasket);
          // On garde tous les joueurs dans la liste : les ambigus auront leur carte
          // "Ver opciones" pour relancer le dialog manuellement si besoin.
          setPlayersData(data.players);
          if (playersWithEurobasketChoice.length > 0) {
            // Ouvrir automatiquement le dialogue pour le premier joueur ambigu ;
            // les autres restent accessibles via leur bouton "Ver opciones".
            const firstEurobasket = playersWithEurobasketChoice[0];
            setCurrentPlayerName(firstEurobasket.nom);
            setEurobasketOptions(firstEurobasket.eurobasketOptions);
            setProballersPlayersData(firstEurobasket.proballersPlayersData || []);
            setPendingPlayerData({ nom: firstEurobasket.nom });
            setOpenEurobasketDialog(true);
          } else {
            setSuccess(true);
          }
          setLoading(false);
          eventSource.close();
        } else if (data.type === 'error') {
          setError(data.message);
          setLoading(false);
          eventSource.close();
        }
      } catch (parseError) {
        console.error('Error parsing SSE:', parseError);
      }
    };

    eventSource.onerror = (err) => {
      console.error('EventSource error:', err);
      setError(t('import.connectionError'));
      setLoading(false);
      eventSource.close();
    };
  };

  // Gestion du choix d'une option Eurobasket
  const handleEurobasketOptionClick = async (option, index) => {
    try {
      setSelectedOptionIndex(index);
      setError(null);

      setLogs((prev) => [...prev, `${t('import.selectedEurobasket')}: ${option.name}`]);
      if (option.birthDate) {
        setLogs((prev) => [...prev, `${t('players.birthDate')}: ${option.birthDate}`]);
      }
      setLogs((prev) => [...prev, `URL: ${option.href || option.link}`]);

      const lang = localStorage.getItem('language') || 'es';
      const resp = await api.post('/api/importar/proballers/save-with-eurobasket', {
        playerData: pendingPlayerData,
        eurobasketUrl: option.href || option.link,
        eurobasketBirthDate: option.birthDate,
        proballersPlayersData: proballersPlayersData,
        lang: lang
      });

      if (resp.data.success) {
        setLogs((prev) => [...prev, `✓ ${t('import.importedSuccess')}`]);
        setSuccess(true);
        // Remplace la carte "multipleEurobasket" du joueur par la carte "importé"
        // (sinon on garde la carte ambiguë + on ajoute un doublon importé).
        setPlayersData((prev) => {
          const imported = {
            nom: pendingPlayerData.nom,
            found: true,
            alreadyInDB: false,
            multiple: false
          };
          const idx = prev.findIndex(p => p.nom === pendingPlayerData.nom && p.multipleEurobasket);
          if (idx === -1) return [...prev, imported];
          const next = [...prev];
          next[idx] = imported;
          return next;
        });
      }
    } catch (err) {
      const errorMessage = translateBackendMessage(
        err.response?.data?.message,
        err.response?.data
      ) || t('import.importError');
      setLogs((prev) => [...prev, `✗ ${t('common.error')}: ${errorMessage}`]);
      setError(errorMessage);
    } finally {
      setLoading(false);
      setSelectedOptionIndex(null);
      setOpenEurobasketDialog(false);
      setPendingPlayerData(null);
      setEurobasketOptions([]);
      setProballersPlayersData([]);
    }
  };

  const handleEurobasketDialogClose = () => {
    setOpenEurobasketDialog(false);
    setPendingPlayerData(null);
    setEurobasketOptions([]);
    setProballersPlayersData([]);
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 2, mb: 2, px: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {t('import.importSuccess')}
        </Alert>
      )}

      {/* Layout en 2 colonnes */}
      <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 140px)' }}>

        {/* Colonne gauche: Instructions + Formulaire */}
        <Box sx={{ flex: '0 0 55%', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>

          {/* Card de instrucciones */}
          <Card elevation={2} sx={{ borderRadius: 2, flex: '0 0 auto' }}>
            <CardContent sx={{ py: 2, px: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <InfoOutlinedIcon color="info" />
                <Typography variant="h6" fontWeight="bold">
                  {t('import.howToUse')}
                </Typography>
              </Box>

              <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                {t('import.importFrom')} <strong>Proballers</strong> {t('import.importFromEurobasket')}.
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                📝 {t('import.instructions')}:
              </Typography>

              <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                <li>
                  <Typography variant="body2">
                    {t('import.writeNames')} <strong>{t('import.onePerLine')}</strong> {t('import.orSeparatedBy')} <strong>{t('import.commas')}</strong>.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t('import.orPasteUrl')} <strong>({t('import.eurobasketUrlExample')})</strong>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t('import.ifThereAre')} <strong>{t('import.multipleResults')}</strong>, {t('import.selectCorrectPlayer')}.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t('import.importedData')}: <Chip label={t('common.name')} size="small" sx={{ mx: 0.3, height: 18, fontSize: '0.7rem' }} />
                    <Chip label={t('common.date')} size="small" sx={{ mx: 0.3, height: 18, fontSize: '0.7rem' }} />
                    <Chip label={t('players.height')} size="small" sx={{ mx: 0.3, height: 18, fontSize: '0.7rem' }} />
                    <Chip label={t('players.position')} size="small" sx={{ mx: 0.3, height: 18, fontSize: '0.7rem' }} />
                  </Typography>
                </li>
              </Box>
            </CardContent>
          </Card>

          {/* Formulaire */}
          <Card elevation={2} sx={{ borderRadius: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
              <form onSubmit={(e) => { e.preventDefault(); handleProballersImport(e); }} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label={t('import.playerNames')}
                  name="details"
                  value={formData.details}
                  onChange={handleChange}
                  required
                  multiline
                  minRows={4}
                  variant="outlined"
                  placeholder={t('import.placeholder')}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    '& .MuiOutlinedInput-root': {
                      height: '100%',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputBase-inputMultiline': {
                      height: '100% !important',
                      overflow: 'auto !important'
                    }
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  disabled={loading || !formData.details.trim()}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    flexShrink: 0,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #63348c 100%)',
                    },
                    '&:disabled': {
                      background: '#cccccc',
                    }
                  }}
                >
                  {loading ? t('import.searching') : t('import.searchAndImport')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Box>

        {/* Colonne droite: Logs + Résultats */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>

          {/* Logs en tiempo real */}
          <Paper
            elevation={2}
            sx={{
              p: 2,
              height: logs.length > 0 ? '40%' : '0',
              overflow: 'auto',
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
              transition: 'height 0.3s ease',
              display: logs.length > 0 ? 'block' : 'none'
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
              📋 {t('import.activityLog')}:
            </Typography>
            {logs.map((line, i) => (
              <Typography variant="body2" key={i} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {line}
              </Typography>
            ))}
            {/* Élément invisible pour le scroll automatique */}
            <div ref={logsEndRef} />
          </Paper>

          {/* Resultados */}
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              {playersData.length > 0 ? (
                <>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                    📊 {t('import.importResults')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {playersData.map((player, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        backgroundColor: player.alreadyInDB
                          ? '#fff3e0'
                          : player.found === false
                          ? '#ffebee'
                          : player.multipleEurobasket
                          ? '#fff3e0'
                          : player.multiple
                          ? '#e3f2fd'
                          : '#e8f5e9'
                      }}
                    >
                      {player.multipleEurobasket ? (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip label={t('import.multipleEurobasketOptions')} sx={{ backgroundColor: '#ff6b35', color: 'white' }} size="small" />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {player.nom}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1, fontSize: '0.85rem' }}>
                            {t('import.multipleEurobasketPlayersFound')}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setCurrentPlayerName(player.nom);
                              setEurobasketOptions(player.eurobasketOptions);
                              setProballersPlayersData(player.proballersPlayersData || []);
                              setPendingPlayerData({ nom: player.nom });
                              setOpenEurobasketDialog(true);
                            }}
                            sx={{
                              background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                            }}
                          >
                            {t('import.viewOptions')} ({player.eurobasketOptions?.length || 0})
                          </Button>
                        </>
                      ) : player.found === false ? (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={t('import.notFound')} color="error" size="small" />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {player.nom}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {t('import.notFoundIn')}
                          </Typography>
                        </>
                      ) : player.alreadyInDB ? (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={t('import.alreadyExists')} color="warning" size="small" />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {player.nom}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {t('import.alreadyInDatabase')}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={`✓ ${t('import.imported')}`} color="success" size="small" />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {player.nom}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {t('import.importedSuccessfully')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  ))}
                </>
              ) : (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1" align="center">
                    {t('import.resultsWillAppear')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Dialog pour options multiples Eurobasket */}
      <Dialog
        open={openEurobasketDialog}
        onClose={handleEurobasketDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)', color: 'white' }}>
          <Typography variant="h6" fontWeight="bold">
            {t('import.selectEurobasketPlayer')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {currentPlayerName}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('import.multipleEurobasketPlayersFound')}
          </Alert>
          <List>
            {eurobasketOptions.map((option, idx) => (
              <ListItem
                key={idx}
                sx={{
                  mb: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#fff3e0'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="bold">
                      {option.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {option.birthDate && <strong>{t('players.birthDate')}: {option.birthDate}</strong>}
                        {option.team && ` • ${t('players.team')}: ${option.team}`}
                        {option.position && ` • ${t('players.position')}: ${option.position}`}
                        {option.height && ` • ${option.height}`}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                        {option.href || option.link}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={t('common.viewProfile') || 'Ver ficha'}>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(option.href || option.link, '_blank');
                      }}
                      sx={{ color: '#667eea' }}
                      disabled={selectedOptionIndex !== null}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('common.select') || 'Seleccionar'}>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEurobasketOptionClick(option, idx);
                      }}
                      sx={{ color: selectedOptionIndex === idx ? '#ff9800' : '#4caf50' }}
                      disabled={selectedOptionIndex !== null}
                    >
                      {selectedOptionIndex === idx ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <CheckCircleIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleEurobasketDialogClose} variant="outlined">
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Importar;
